import { createContext, useContext, useMemo, type ReactNode } from "react";

import useDashboardCustomization from "./hooks/hooks";
import type { DashboardCustomizationProps } from "./types";

type DashboardCustomizationContextValue = ReturnType<
  typeof useDashboardCustomization
>;

const DashboardCustomizationContext =
  createContext<DashboardCustomizationContextValue | null>(null);

export const DashboardCustomizationProvider = ({
  externalAccounts,
  servicesApps,
  children
}: DashboardCustomizationProps & {
  children: ReactNode;
}) => {
  const value = useDashboardCustomization({ externalAccounts, servicesApps });
  const memoized = useMemo(
    () => value,
    [
      value.isCustomizeDialogOpen,
      value.isCustomizeLoading,
      value.openDialogHandler,
      value.closeDialogHandler,
      value.submitCustomizationHandler,
      value.leftOrder,
      value.rightOrder,
      value.order,
      value.setOrder,
      value.resetOrder,
      value.setLeftOrder,
      value.setRightOrder,
      value.getSectionSize
    ]
  );

  return (
    <DashboardCustomizationContext.Provider value={memoized}>
      {children}
    </DashboardCustomizationContext.Provider>
  );
};

export const useDashboardCustomizationContext =
  (): DashboardCustomizationContextValue => {
    const ctx = useContext(DashboardCustomizationContext);
    if (!ctx) {
      throw new Error(
        "useDashboardCustomizationContext must be used within a DashboardCustomizationProvider"
      );
    }
    return ctx;
  };
